import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  ListSubheader
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { showGlobalNotification } from '../context/NotificationContext.js';
import { useOrg } from '../context/OrgContext';

const API = process.env.REACT_APP_API_URL;
const CUSTOM = '__custom__';
// Agreement lines first; the rest fill in what the agreement does not price
const sourceGroupsFor = (orgName) => [
  { source: 'agreement', title: 'Rate agreement' },
  { source: 'vendor', title: "Provider's default rates" },
  { source: 'default', title: `${orgName} default rates` }
];
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => parseFloat(Number(n).toFixed(2));
const isMileageLine = (item) => /^(En Route Per Mile|Loaded Mileage)/.test(item.item || '');
const isEnRouteLine = (item) => /^En Route Per Mile/.test(item.item || '');
const isFreeLine = (item) => item.isFreeMiles || /Free Miles$/.test(item.item || '');
const isPending = (item) => item.approvalStatus === 'pending';

const totalsFrom = (items, tax) => {
  const subtotal = round2(items.filter((i) => !isPending(i)).reduce((sum, i) => sum + num(i.total), 0));
  return { subtotal, total: round2(subtotal + num(tax)) };
};

/** Keeps the free-miles credit in step with the mileage lines (one allowance across en route + loaded). */
const syncFreeMiles = (items, allowance) => {
  const free = items.find(isFreeLine);
  if (!free) return items;
  const mileageQty = items.filter(isMileageLine).reduce((sum, i) => sum + num(i.qty), 0);
  const rate = num(items.find(isEnRouteLine)?.price);
  const qty = Math.min(num(allowance), mileageQty);
  return items.map((i) => (i === free ? { ...i, qty: round2(qty), price: rate, total: -round2(qty * rate), isFreeMiles: true } : i));
};

const EditInvoiceDialog = ({ open, onClose, jobId, onSave }) => {
  const { name: orgName } = useOrg();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [header, setHeader] = useState({ providerBillable: 'Fully Billable', billingType: 'ACH', classType: 'Light Duty', equipment: 'N/A', invoiceNotes: '', tax: 0 });
  const [items, setItems] = useState([]);
  const [savedCount, setSavedCount] = useState(0);
  const [allowance, setAllowance] = useState(0);
  const [options, setOptions] = useState([]);
  const [canEditFully, setCanEditFully] = useState(false);
  const [canAddFees, setCanAddFees] = useState(false);
  const [draft, setDraft] = useState({ rateKey: '', item: '', qty: 1, price: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [jobRes, optRes] = await Promise.all([
        axios.get(`${API}/api/jobs/${jobId}`),
        axios.get(`${API}/api/jobs/${jobId}/invoice-rate-options`)
      ]);
      let invoice = jobRes.data.invoice;
      if (!invoice || !Array.isArray(invoice.items) || invoice.items.length === 0) {
        const details = await axios.get(`${API}/api/jobs/${jobId}/invoice-details`);
        invoice = details.data.invoice || details.data || {};
      }
      const loaded = (invoice.items || []).map((i) => ({ ...i, qty: num(i.qty), total: num(i.total) }));
      setItems(loaded);
      setSavedCount(loaded.length);
      setAllowance(invoice.freeMiles ?? num(loaded.find(isFreeLine)?.qty));
      setHeader({
        providerBillable: invoice.providerBillable || 'Fully Billable',
        billingType: invoice.billingType || 'ACH',
        classType: invoice.classType || 'Light Duty',
        equipment: invoice.equipment || 'N/A',
        invoiceNotes: invoice.invoiceNotes || '',
        tax: num(invoice.tax)
      });
      setOptions(optRes.data.options || []);
      setCanEditFully(Boolean(optRes.data.canEditFully));
      setCanAddFees(Boolean(optRes.data.canAddFees));
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError(err.response?.data?.message || 'Could not load the invoice.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (open && jobId) {
      setDraft({ rateKey: '', item: '', qty: 1, price: '' });
      load();
    }
  }, [open, jobId, load]);

  const { subtotal, total } = totalsFrom(items, header.tax);

  const changeLine = (index, field, value) => {
    setItems((prev) => {
      const next = prev.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        if (field === 'qty' || field === 'price') {
          updated.total = round2(num(field === 'qty' ? value : item.qty) * num(field === 'price' ? value : item.price));
        }
        return updated;
      });
      return syncFreeMiles(next, allowance);
    });
  };

  const removeLine = (index) => setItems((prev) => syncFreeMiles(prev.filter((_, i) => i !== index), allowance));
  const approveLine = (index) => setItems((prev) => prev.map((item, i) => (i === index ? { ...item, approvalStatus: 'approved', approve: true } : item)));

  const selectedOption = options.find((o) => o.key === draft.rateKey);
  const draftPrice = selectedOption ? selectedOption.rate : num(draft.price);
  const draftLabel = selectedOption ? selectedOption.label : draft.item.trim();
  const draftValid = draftLabel && num(draft.qty) > 0 && (selectedOption || draft.price !== '');

  const addFee = () => {
    if (!draftValid) return;
    const qty = num(draft.qty);
    setItems((prev) => [
      ...prev,
      {
        item: draftLabel,
        rateKey: selectedOption?.key,
        qty,
        price: draftPrice,
        total: round2(qty * draftPrice),
        approvalStatus: selectedOption ? 'agreed' : canEditFully ? 'approved' : 'pending',
        isNew: true
      }
    ]);
    setDraft({ rateKey: '', item: '', qty: 1, price: '' });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = canEditFully
        ? { ...header, items, subtotal, total }
        : { items: items.map(({ isNew, ...rest }) => rest) };
      await axios.put(`${API}/api/jobs/${jobId}`, { invoice: payload });
      if (onSave) onSave(jobId);
      onClose();
    } catch (err) {
      console.error('Error saving invoice:', err);
      showGlobalNotification(err.response?.data?.message || 'Invoice could not be saved. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const lineEditable = (item, index) => canEditFully && !isFreeLine(item);
  const lineRemovable = (item, index) => (canEditFully && !isFreeLine(item)) || (item.isNew && index >= savedCount);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{canEditFully ? 'Edit Invoice' : 'Add Fees to Invoice'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}><Typography>Loading invoice...</Typography></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {!canEditFully && (
              <Alert severity="info" sx={{ mb: 2 }}>
                The fees {orgName} calculated cannot be changed. You can add fees from your rate agreement; a fee without an agreed rate is sent to {orgName} for approval and is not paid until approved.
              </Alert>
            )}

            {canEditFully && (
              <Box mb={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Provider Billable</InputLabel>
                      <Select value={header.providerBillable} label="Provider Billable" onChange={(e) => setHeader((h) => ({ ...h, providerBillable: e.target.value }))}>
                        {['Fully Billable', 'Partially Billable', 'Not Billable'].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Billing Type</InputLabel>
                      <Select value={header.billingType} label="Billing Type" onChange={(e) => setHeader((h) => ({ ...h, billingType: e.target.value }))}>
                        {['ACH', 'Check', 'Credit Card', 'Invoice'].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Class Type</InputLabel>
                      <Select value={header.classType} label="Class Type" onChange={(e) => setHeader((h) => ({ ...h, classType: e.target.value }))}>
                        {['Light Duty', 'Medium Duty', 'Heavy Duty'].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Equipment" value={header.equipment} onChange={(e) => setHeader((h) => ({ ...h, equipment: e.target.value }))} />
                  </Grid>
                </Grid>
              </Box>
            )}

            <Typography variant="h6" gutterBottom>Items</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index} sx={isPending(item) ? { backgroundColor: 'warning.light' } : undefined}>
                      <TableCell>
                        {lineEditable(item, index) ? (
                          <TextField fullWidth size="small" variant="standard" value={item.item} onChange={(e) => changeLine(index, 'item', e.target.value)} />
                        ) : (
                          <Typography variant="body2">{item.item}</Typography>
                        )}
                        {isPending(item) && (
                          <Chip size="small" color="warning" label={`Pending ${orgName} approval`} sx={{ mt: 0.5 }} />
                        )}
                        {item.approvalStatus === 'agreed' && <Chip size="small" label="Agreed rate" sx={{ mt: 0.5 }} />}
                        {item.addedByName && <Typography variant="caption" color="text.secondary" display="block">Added by {item.addedByName}</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        {lineEditable(item, index) ? (
                          <TextField size="small" variant="standard" type="number" inputProps={{ min: 0, step: 0.1 }} value={item.qty} onChange={(e) => changeLine(index, 'qty', num(e.target.value))} sx={{ width: 80 }} />
                        ) : (
                          <Typography variant="body2">{item.qty}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {lineEditable(item, index) && typeof item.price !== 'string' ? (
                          <TextField size="small" variant="standard" type="number" inputProps={{ step: 0.01 }} value={item.price} onChange={(e) => changeLine(index, 'price', num(e.target.value))} sx={{ width: 90 }} />
                        ) : (
                          <Typography variant="body2">{typeof item.price === 'string' ? item.price : `$${num(item.price).toFixed(2)}`}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right"><Typography variant="body2">${num(item.total).toFixed(2)}</Typography></TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {canEditFully && isPending(item) && (
                          <Button size="small" onClick={() => approveLine(index)}>Approve</Button>
                        )}
                        {lineRemovable(item, index) && (
                          <IconButton size="small" color="error" onClick={() => removeLine(index)} aria-label="remove item"><CloseIcon fontSize="small" /></IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {(canEditFully || canAddFees) && (
              <Box mt={2} p={2} component={Paper} variant="outlined">
                <Typography variant="subtitle2" gutterBottom>Add a fee</Typography>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Item</InputLabel>
                      <Select
                        value={draft.rateKey}
                        label="Item"
                        onChange={(e) => setDraft((d) => ({ ...d, rateKey: e.target.value, item: '', price: '' }))}
                      >
                        {options.length === 0 && <MenuItem disabled value="">No agreed rates found for this job</MenuItem>}
                        {sourceGroupsFor(orgName).flatMap(({ source, title }) => {
                          const group = options.filter((o) => o.source === source);
                          if (group.length === 0) return [];
                          return [
                            // Select gives every child option semantics; keep the heading out of reach of clicks and arrow keys
                            <ListSubheader key={`h-${source}`} disableSticky muiSkipListHighlight sx={{ pointerEvents: 'none', lineHeight: '36px' }}>{title}</ListSubheader>,
                            ...group.map((o) => (
                              <MenuItem key={o.key} value={o.key}>{o.label} — ${num(o.rate).toFixed(2)}</MenuItem>
                            ))
                          ];
                        })}
                        <MenuItem value={CUSTOM}>Other (no agreed rate)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {draft.rateKey === CUSTOM && (
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth size="small" label="Describe the fee" value={draft.item} onChange={(e) => setDraft((d) => ({ ...d, item: e.target.value }))} />
                    </Grid>
                  )}
                  <Grid item xs={4} sm={1}>
                    <TextField fullWidth size="small" label="Qty" type="number" inputProps={{ min: 0, step: 0.1 }} value={draft.qty} onChange={(e) => setDraft((d) => ({ ...d, qty: e.target.value }))} />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Price"
                      type="number"
                      inputProps={{ step: 0.01 }}
                      value={selectedOption ? selectedOption.rate : draft.price}
                      disabled={Boolean(selectedOption)}
                      helperText={selectedOption ? (selectedOption.source === 'default' ? 'Default rate' : 'Agreed rate') : draft.rateKey === CUSTOM && !canEditFully ? 'Needs approval' : ' '}
                      onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={4} sm={1}>
                    <Button startIcon={<AddIcon />} onClick={addFee} disabled={!draftValid} size="small">Add</Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {canEditFully && (
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>Invoice Notes</Typography>
                <TextField fullWidth multiline rows={3} size="small" value={header.invoiceNotes} onChange={(e) => setHeader((h) => ({ ...h, invoiceNotes: e.target.value }))} />
              </Box>
            )}

            <Box mt={3}>
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Subtotal:</Typography>
                    <Typography>${subtotal.toFixed(2)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography>Tax</Typography>
                    {canEditFully ? (
                      <TextField size="small" variant="standard" type="number" inputProps={{ min: 0, step: 0.01 }} value={header.tax} onChange={(e) => setHeader((h) => ({ ...h, tax: num(e.target.value) }))} sx={{ width: 80 }} />
                    ) : (
                      <Typography>${num(header.tax).toFixed(2)}</Typography>
                    )}
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">Invoice Total:</Typography>
                    <Typography variant="h6">${total.toFixed(2)}</Typography>
                  </Box>
                  {items.some(isPending) && (
                    <Typography variant="caption" color="text.secondary">Fees pending approval are not included in the total.</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">CANCEL</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={loading || saving || Boolean(error)}>SAVE</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInvoiceDialog;
